import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Trophy, Users2, Building2, Star, Clock, Hash, CheckCircle2,
  TrendingUp, BarChart3, Lock,
} from 'lucide-react';

const MEDAL = ['🥇', '🥈', '🥉'];

function ScoreBar({ value, max = 100 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color = pct >= 75 ? '#39b563' : pct >= 50 ? '#5780ea' : pct >= 30 ? '#fbbf24' : '#f97373';
  return (
    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function TeamPerformancePage() {
  const [period, setPeriod] = useState('week');
  const [team, setTeam] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [department, setDepartment] = useState('');

  const load = async () => {
    const params = new URLSearchParams({ period });
    if (department) params.set('department', department);
    const [t, lb] = await Promise.all([
      api.get(`/daily-reports/stats/team?${params.toString()}`),
      api.get(`/daily-reports/stats/leaderboard?period=${period}`),
    ]);
    setTeam(t.data);
    setLeaderboard(lb.data.leaderboard || []);
  };

  useEffect(() => { load(); }, [period, department]);

  const departments = useMemo(() => {
    if (!team?.by_department) return [];
    return Object.entries(team.by_department).sort((a, b) => b[1].items - a[1].items);
  }, [team]);

  const topClients = useMemo(() => {
    if (!team?.by_client) return [];
    return Object.entries(team.by_client).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [team]);

  const topFormats = useMemo(() => {
    if (!team?.by_format) return [];
    return Object.entries(team.by_format).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [team]);

  const maxScore = leaderboard.length ? Math.max(...leaderboard.map((x) => x.score)) : 100;
  const maxClientItems = topClients.length ? topClients[0][1] : 1;
  const maxFormatItems = topFormats.length ? topFormats[0][1] : 1;

  return (
    <div>
      <PageHeader
        icon={Trophy}
        title="Team Performance"
        subtitle="Leaderboard, department & client breakdowns based on daily reports."
        actions={(
          <div className="flex gap-2">
            {['today', 'week', 'month'].map((p) => (
              <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'}
                onClick={() => setPeriod(p)}
                className={period === p ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'}
                data-testid={`tp-period-${p}`}>
                {p === 'today' ? 'Today' : p === 'week' ? 'Week' : 'Month'}
              </Button>
            ))}
          </div>
        )}
      />

      {!team ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <>
          {/* Top-line metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <Card className="shine-card p-4 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-slate-400">Items</div>
              <div className="text-2xl font-extrabold text-white mt-1">{team.total_items}</div>
              <div className="text-xs text-slate-400">{team.entries} entries</div>
            </Card>
            <Card className="shine-card p-4 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-slate-400">Hours</div>
              <div className="text-2xl font-extrabold text-white mt-1">
                {team.hours_hidden ? (
                  <span className="inline-flex items-center gap-1 text-slate-400"><Lock className="h-5 w-5" />—</span>
                ) : (team.total_hours ?? 0)}
              </div>
              {team.hours_hidden && <div className="text-[11px] text-slate-500 mt-1">Restricted by your access</div>}
            </Card>
            <Card className="shine-card p-4 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-slate-400">Completed</div>
              <div className="text-2xl font-extrabold text-emerald-300 mt-1">{team.completed}</div>
              <div className="text-xs text-slate-400">{team.ongoing} ongoing</div>
            </Card>
            <Card className="shine-card p-4 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-slate-400">Submitted</div>
              <div className="text-2xl font-extrabold text-sky-300 mt-1">{team.submitted}</div>
            </Card>
            <Card className="shine-card p-4 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-slate-400">Avg rating</div>
              <div className="text-2xl font-extrabold text-white mt-1">{team.avg_rating ? `${team.avg_rating}/5` : '—'}</div>
              <div className="text-xs text-slate-400">{team.ratings_count} reviews</div>
            </Card>
            <Card className="shine-card p-4 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-slate-400">Days w/ activity</div>
              <div className="text-2xl font-extrabold text-white mt-1">{team.days_reported}</div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Leaderboard */}
            <Card className="shine-card p-5 rounded-xl lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-5 w-5 text-amber-300" />
                <h3 className="font-bold text-white">Leaderboard</h3>
                <Badge variant="outline" className="border-amber-500/40 text-amber-200 ml-auto">{leaderboard.length} members</Badge>
              </div>
              {leaderboard.length === 0 ? (
                <p className="text-slate-400 text-sm">No activity in this period.</p>
              ) : (
                <ul className="divide-y divide-amber-500/5">
                  {leaderboard.map((u, i) => (
                    <li key={u.user_id} className="py-3" data-testid={`leaderboard-row-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-7 text-center text-lg">{i < 3 ? MEDAL[i] : <span className="text-slate-500 text-sm">{i + 1}</span>}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-white truncate">{u.user_name}</span>
                            <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{u.user_role}</Badge>
                          </div>
                          <div className="mt-1 text-xs text-slate-400 flex flex-wrap items-center gap-x-3">
                            <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> {u.items}</span>
                            {u.hours_hidden ? (
                              <span className="inline-flex items-center gap-1 text-slate-500"><Lock className="h-3 w-3" />—</span>
                            ) : (
                              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {u.hours}h</span>
                            )}
                            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {u.completed} done</span>
                            <span>· {u.days_reported} days</span>
                            {u.avg_rating && <span className="inline-flex items-center gap-1"><Star className="h-3 w-3" /> {u.avg_rating}</span>}
                          </div>
                          <div className="mt-2"><ScoreBar value={u.score} max={maxScore || 100} /></div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-extrabold shine-gold-text">{u.score.toFixed(0)}</div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">score</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Department breakdown */}
            <Card className="shine-card p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Users2 className="h-5 w-5 text-amber-300" />
                <h3 className="font-bold text-white">By Department</h3>
              </div>
              <div className="mb-3">
                <Select value={department || '_all'} onValueChange={(v) => setDepartment(v === '_all' ? '' : v)}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    <SelectItem value="_all">All departments</SelectItem>
                    {departments.map(([role]) => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {departments.length === 0 ? (
                <p className="text-slate-400 text-sm">No data.</p>
              ) : (
                <ul className="space-y-3">
                  {departments.map(([role, d]) => {
                    const max = Math.max(...departments.map(([, x]) => x.items));
                    const pct = max ? (d.items / max) * 100 : 0;
                    return (
                      <li key={role}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-200">{role}</span>
                          <span className="text-amber-300">
                            {d.items} items
                            {d.hours !== undefined ? ` · ${d.hours}h` : ''}
                          </span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#39b563' }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            {/* Top clients */}
            <Card className="shine-card p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-5 w-5 text-amber-300" />
                <h3 className="font-bold text-white">Top Clients (items delivered)</h3>
              </div>
              {topClients.length === 0 ? (
                <p className="text-slate-400 text-sm">No data.</p>
              ) : (
                <ul className="space-y-3">
                  {topClients.map(([name, items]) => (
                    <li key={name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-200">{name}</span>
                        <span className="text-amber-300">{items} items</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(items / maxClientItems) * 100}%`, background: '#5780ea' }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Top formats */}
            <Card className="shine-card p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-5 w-5 text-amber-300" />
                <h3 className="font-bold text-white">Top Formats</h3>
              </div>
              {topFormats.length === 0 ? (
                <p className="text-slate-400 text-sm">No data.</p>
              ) : (
                <ul className="space-y-3">
                  {topFormats.map(([name, items]) => (
                    <li key={name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-200">{name}</span>
                        <span className="text-amber-300">{items}</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(items / maxFormatItems) * 100}%`, background: '#fbbf24' }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
